<template>
  <v-card class="fill-height w-100">
    <v-toolbar v-if="!hideToolbar" density="compact">
      <v-toolbar-title>
        <v-icon size="small" start>{{ icon }}</v-icon
        >{{ title }}
      </v-toolbar-title>

      <v-toolbar-items>
        <v-tooltip v-if="tooltip" :text="tooltip">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              icon="mdi-information-slab-circle"
              variant="plain"
              v-bind="tooltipProps"
            />
          </template>
        </v-tooltip>
      </v-toolbar-items>
    </v-toolbar>

    <v-card-text>
      <slot name="content">
        <div
          v-if="animated && isNumeric"
          :class="[textSize, 'font-weight-bold']"
        >
          <animated-integer :formatter="formatFn" :value="numericValue" />
        </div>

        <div
          v-else-if="hasDisplayValue"
          :class="[textSize, 'font-weight-bold', 'text-no-wrap']"
        >
          {{ formattedValue }}
        </div>
        <div v-else :class="[textSize, 'font-weight-bold']">--</div>
      </slot>
      <v-divider v-if="description" class="my-2 w-100" />
      <span class="text-body-small text-grey font-weight-black">{{
        description
      }}</span>
    </v-card-text>
    <slot name="card-actions" />
  </v-card>
</template>

<script setup>
  import { computed } from 'vue'

  const props = defineProps({
    animated: { default: false, type: Boolean },
    color: { default: null, type: String },
    description: { type: String },
    hideToolbar: { default: false, type: Boolean },
    icon: { type: String },
    locale: { default: 'en-US', type: String },
    textSize: { default: 'text-headline-large', type: String },
    title: { type: String },
    tooltip: { type: String },
    units: { default: 'number', type: String },
    value: { default: null, type: [Number, String, null] },
    variant: { default: 'elevated', type: String },
  })

  function toNum(v) {
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : null
  }

  const nf = (locale, opts) => new Intl.NumberFormat(locale, opts)

  function formatCurrency(locale, currency = 'USD') {
    return (n) =>
      n == null
        ? '--'
        : nf(locale, {
            currency,
            maximumFractionDigits: 0,
            style: 'currency',
          }).format(n)
  }

  function formatNumber(locale, maxFrac = 0) {
    return (n) =>
      n == null
        ? '--'
        : nf(locale, { maximumFractionDigits: maxFrac }).format(n)
  }

  function formatPercent(locale) {
    return (n) =>
      n == null
        ? '--'
        : nf(locale, { maximumFractionDigits: 2 }).format(n) + '%'
  }

  const BYTES_IN_KB = 1024
  const BYTES_IN_MB = 1024 ** 2
  const BYTES_IN_GB = 1024 ** 3

  function formatGB(locale, maxFrac = 2) {
    return (n) =>
      n == null
        ? '--'
        : `${nf(locale, { maximumFractionDigits: maxFrac }).format(n / BYTES_IN_GB)} GB`
  }

  function formatKB(locale, maxFrac = 2) {
    return (n) =>
      n == null
        ? '--'
        : `${nf(locale, { maximumFractionDigits: maxFrac }).format(n / BYTES_IN_KB)} KB`
  }
  function formatMB(locale, maxFrac = 2) {
    return (n) =>
      n == null
        ? '--'
        : `${nf(locale, { maximumFractionDigits: maxFrac }).format(n / BYTES_IN_MB)} MB`
  }

  const numericValue = computed(() => toNum(props.value))
  const isNumeric = computed(() => numericValue.value !== null)

  // show non-empty strings too (including "0", etc.)
  const hasDisplayValue = computed(
    () =>
      props.value !== null &&
      props.value !== undefined &&
      String(props.value) !== ''
  )

  const formatFn = computed(() => {
    const locale = props.locale
    const val = numericValue.value

    switch (props.units) {
      case 'bytes': {
        if (val !== null) {
          if (Math.abs(val) < BYTES_IN_MB) {
            return formatKB(locale, 2)
          } else if (Math.abs(val) < BYTES_IN_GB * 0.75) {
            return formatMB(locale, 2)
          } else {
            return formatGB(locale, 2)
          }
        }
        return () => '--'
      }
      case 'dollars': {
        return formatCurrency(locale, 'USD')
      }
      case 'gb': {
        return formatGB(locale, 2)
      }
      case 'mb': {
        return formatMB(locale, 2)
      }
      case 'metric': {
        return val !== null && Math.abs(val) < 1000 * 3
          ? (n) => `${formatNumber(locale, 2)(n)} MB`
          : (n) => `${formatNumber(locale, 2)(n)} GB`
      }
      case 'number': {
        return formatNumber(locale, 0)
      }
      case 'percent': {
        return formatPercent(locale)
      }
      default: {
        // identity for strings; fallback to '--' for null/undefined
        return (v) =>
          v === null || v === undefined || String(v) === '' ? '--' : String(v)
      }
    }
  })

  const formattedValue = computed(() => {
    const val = isNumeric.value ? numericValue.value : props.value
    return formatFn.value(val)
  })
</script>
